import { NavLink } from "react-router-dom";
import SectionHeader from "../components/SectionHeader";
import { routePaths } from "../constants/routes";
import { useAuth } from "../hooks/useAuth";
import "../style/dashboard.css";

const learningCards = [
  {
    title: "Continue Automation Practice",
    description: "Pick up hands-on modules for alerts, forms, waits, frames, widgets, and UI actions.",
    to: routePaths.automationTesting,
    meta: "Practice lab",
  },
  {
    title: "Review Interview Questions",
    description: "Prepare answers for manual testing, automation, API testing, and project discussions.",
    to: routePaths.interviewQuestions,
    meta: "Interview prep",
  },
  {
    title: "Revise Study Notes",
    description: "Use short notes to quickly refresh QA concepts before practice or interviews.",
    to: routePaths.studyNotes,
    meta: "Revision",
  },
  {
    title: "Raise or Track Bugs",
    description: "Practice professional defect reporting with status, priority, severity, and comments.",
    to: routePaths.bugs,
    meta: "Bug tracker",
  },
];

const recommendedModules = [
  "Manual Testing Foundation",
  "Automation Locators and Waits",
  "API Testing Basics",
  "Bug Reporting Practice",
  "Mock Interview Preparation",
];

export default function StudentDashboard() {
  const { user, userData, goal, experienceLevel, interestedTrack, profileCompleted } = useAuth();
  const selectedGoal = goal || interestedTrack || "manual";
  const progressItems = [
    { label: "Profile setup", value: profileCompleted ? "100%" : "40%" },
    { label: "Learning track", value: selectedGoal || "Manual QA" },
    { label: "Practice status", value: "Ready" },
    { label: "Interview prep", value: "Start now" },
  ];

  const trackPlan = {
    manual: ["SDLC and STLC", "Test scenarios", "Test cases", "Bug reports"],
    automation: ["Locators", "Forms", "Waits", "Alerts and frames"],
    api: ["HTTP methods", "Status codes", "Postman basics", "Assertions"],
    interview: ["Manual QA Q&A", "API Q&A", "Automation Q&A", "Mock interview plan"],
  };

  const activePlan = trackPlan[selectedGoal] || trackPlan.manual;

  return (
    <main className="learner-dashboard">
      <section className="dashboard-hero surface-card">
        <div>
          <span className="ui-badge">Student Dashboard</span>
          <h1>Welcome back, {userData?.fullName || user?.displayName || "QA Learner"}.</h1>
          <p>
            Continue your QA roadmap, open recent practice areas, and use recommended
            topics to move from learning into interview-ready confidence.
          </p>
          <div className="dashboard-actions">
            <NavLink className="btn-ui" to={routePaths.automationTesting}>
              Continue Practice
            </NavLink>
            <NavLink className="btn-ui-secondary" to={routePaths.enroll}>
              Book Course Demo
            </NavLink>
            <NavLink className="btn-ui-secondary" to={routePaths.bugsCreate}>
              Raise Bug
            </NavLink>
          </div>
        </div>
        <div className="dashboard-profile-panel">
          <strong>{profileCompleted ? "Profile ready" : "Profile needs update"}</strong>
          <span>Goal: {goal || "Choose a learning goal"}</span>
          <span>Level: {experienceLevel || "Not selected"}</span>
          <span>Track: {interestedTrack || "Manual / Automation / API"}</span>
        </div>
      </section>

      <section className="dashboard-section">
        <div className="dashboard-mini-stat-grid">
          {progressItems.map((item) => (
            <article className="surface-card dashboard-mini-stat" key={item.label}>
              <span>{item.label}</span>
              <strong>{item.value}</strong>
            </article>
          ))}
        </div>
      </section>

      <section className="dashboard-section">
        <SectionHeader
          eyebrow="Continue Learning"
          title="Your next best actions"
          description="These shortcuts are designed for repeat learners who want to resume quickly."
        />
        <div className="dashboard-card-grid">
          {learningCards.map((card) => (
            <article className="dashboard-learning-card surface-card" key={card.title}>
              <span>{card.meta}</span>
              <h2>{card.title}</h2>
              <p>{card.description}</p>
              <NavLink to={card.to}>Open now</NavLink>
            </article>
          ))}
        </div>
      </section>

      <section className="dashboard-section dashboard-split">
        <div className="surface-card dashboard-list-card">
          <h2>Your personalized track</h2>
          <p className="dashboard-muted-text">
            Based on your selected goal, start with these modules and then continue practice.
          </p>
          <ul>
            {activePlan.map((module) => (
              <li key={module}>{module}</li>
            ))}
          </ul>
        </div>
        <div className="surface-card dashboard-list-card">
          <h2>Recommended modules</h2>
          <ul>
            {recommendedModules.map((module) => (
              <li key={module}>{module}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="dashboard-section dashboard-split reverse">
        <div className="surface-card dashboard-cta-card">
          <span className="ui-badge">Need mentor support?</span>
          <h2>Turn your learning into a guided QA career plan.</h2>
          <p>Ask for course guidance, project support, mock interviews, and resume direction.</p>
          <NavLink className="btn-ui" to={routePaths.contact}>
            Talk to Mentor
          </NavLink>
        </div>
        <div className="surface-card dashboard-list-card">
          <h2>Student checklist</h2>
          <ul>
            <li>Complete one practice module today</li>
            <li>Revise 10 interview questions</li>
            <li>Write 3 test cases for a real app flow</li>
            <li>Book a demo if you want guided learning</li>
          </ul>
        </div>
      </section>
    </main>
  );
}
